import { ExperimentFeature, OrgOrUmbrellaID } from '@fullstory/proto/fs/services/orgs';
import { useNotifications } from '@fullstory/react-hooks';
import {
  AppDispatch,
  ExperimentRequestActions,
  fullstorySelectors,
  settingsSelectors,
} from '@fullstory/redux';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const { getOrgId, getUmbrellaId } = fullstorySelectors;
const { getExperimentConfig } = settingsSelectors;

export const useExperimentConfig = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const umbrellaId = useSelector(getUmbrellaId);
  const notifications = useNotifications();
  const intl = useIntl();
  const experimentConfig = useSelector(getExperimentConfig);

  const featureToMessage: Record<ExperimentFeature, { success: string; failure: string }> = useMemo(
    () => ({
      [ExperimentFeature.FEATURE_INVALID]: {
        success: intl.formatMessage({
          defaultMessage: 'Invalid Feature',
          id: 'cODMxX',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Invalid Feature',
          id: 'cODMxX',
        }),
      },
      [ExperimentFeature.FEATURE_SESSION_SUMMARY]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated Summary Notes',
          id: 'lcP3ds',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update Summary Notes',
          id: 'qQOifD',
        }),
      },
      [ExperimentFeature.FEATURE_PLAYLIST_SUMMARIES]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated Summaries',
          id: 'QzusjM',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update Summaries',
          id: 'er9iWb',
        }),
      },
      [ExperimentFeature.FEATURE_SELECTOR_TRIM]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated Selector Optimization',
          id: 'UKwyV9',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update Selector Optimization',
          id: 'L8N0/G',
        }),
      },
      [ExperimentFeature.FEATURE_SUGGESTED_ELEMENTS]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated Suggested Elements',
          id: 'HEINMI',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update Suggested Elements',
          id: 'MR59mX',
        }),
      },
      [ExperimentFeature.FEATURE_SUGGESTED_LABELS]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated Recommended Object Names',
          id: '2T9XE3',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update Recommended Object Names',
          id: 'cyZcfV',
        }),
      },
      [ExperimentFeature.FEATURE_ALL]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated StoryAI Opt-out',
          id: 'MVlng+',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update StoryAI Opt-out',
          id: 'HRaWnN',
        }),
      },
      [ExperimentFeature.FEATURE_SUGGESTED_PAGES]: {
        success: intl.formatMessage({
          defaultMessage: 'Updated Suggested Pages',
          id: '6fmdgR',
        }),
        failure: intl.formatMessage({
          defaultMessage: 'Failed to update Suggested Pages',
          id: 'fS4BTH',
        }),
      },
    }),
    [intl],
  );

  useEffect(() => {
    try {
      dispatch(ExperimentRequestActions.getAllExperimentsOptedOut({ orgId }));
      if (umbrellaId) {
        dispatch(ExperimentRequestActions.getAllExperimentsOptedOut({ umbrellaId }));
      }
    } catch (e) {
      // todo: consider adding a notification here. For now, we will silently default to opt out
      console.error('failed to get experiment opt outs', e);
    }
  }, [dispatch, orgId, umbrellaId]);

  const setExperimentOptedOut = useCallback(
    async (entityId: OrgOrUmbrellaID, feature: ExperimentFeature, optedOut: boolean) => {
      try {
        await dispatch(
          ExperimentRequestActions.setExperimentOptedOut(entityId, feature, optedOut, true),
        );
        notifications.success(featureToMessage[feature].success);
      } catch (e) {
        console.error('failed to set experiment opted out', e);
        notifications.error(featureToMessage[feature].failure);
      }
    },
    [dispatch, notifications, featureToMessage],
  );

  return {
    experimentOptedOut: experimentConfig,
    setExperimentOptedOut,
  };
};
